import { useEffect, useMemo, useRef, useState } from 'react'

const MAX_MATCHES = 8

// Drop generic filler so the airport reads as a recognizable place name:
// "Milan Malpensa International Airport" -> "Milan Malpensa".
export function cleanName(name) {
  const stripped = (name || '')
    .replace(/\b(International|Intl|Airport)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    // Normalize non-English city spellings to their recognizable English form.
    .replace(/\bMilano\b/g, 'Milan')
  return stripped || name || ''
}

export function airportLabel(a) {
  return `${cleanName(a.name)} (${a.iata})`
}

// Client-side typeahead over the airports list already shipped via /api/meta.
// Matches on airport name, served city, IATA code, country, and keyword tokens
// (so "Tokyo" finds Narita, "New York" finds Newark); ranks IATA/prefix hits
// first. Selecting an airport calls onSelect(iata) — the stored state stays a
// plain IATA string, so the engine call is unchanged.
export default function AirportField({ airports = [], value, onSelect }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)

  const selected = useMemo(
    () => (value ? airports.find((a) => a.iata === value) : null),
    [airports, value]
  )

  // Reflect an externally-set selection (e.g. a shared-URL load) in the input
  // whenever the field isn't being actively edited.
  useEffect(() => {
    if (!open) setQuery(selected ? airportLabel(selected) : '')
  }, [selected, open])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const scored = []
    for (const a of airports) {
      const name = cleanName(a.name).toLowerCase()
      const rawName = (a.name || '').toLowerCase()
      const city = (a.city || '').toLowerCase()
      const iata = a.iata.toLowerCase()
      const country = (a.country || '').toLowerCase()
      const kw = (a.kw || '').toLowerCase()
      let rank
      if (iata === q) rank = 0
      else if (iata.startsWith(q)) rank = 1
      else if (city.startsWith(q) || name.startsWith(q)) rank = 2
      else if (city.includes(q) || name.includes(q) || rawName.includes(q)) rank = 3
      else if (kw.includes(q)) rank = 4
      else if (country === q) rank = 5
      else continue
      scored.push({ a, rank })
    }
    scored.sort(
      (x, y) => x.rank - y.rank || cleanName(x.a.name).localeCompare(cleanName(y.a.name))
    )
    return scored.slice(0, MAX_MATCHES).map((s) => s.a)
  }, [airports, query])

  const showList = open && matches.length > 0

  const choose = (a) => {
    onSelect(a.iata)
    setQuery(airportLabel(a))
    setOpen(false)
  }

  const onKeyDown = (e) => {
    if (!showList) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      choose(matches[highlight] || matches[0])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="relative mt-1">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
        autoComplete="off"
        value={query}
        placeholder="Search city or airport — e.g. Shanghai, PVG"
        onChange={(e) => {
          setQuery(e.target.value)
          setHighlight(0)
          setOpen(true)
          // The stored selection is kept while searching; it only changes when
          // a new airport is explicitly picked. Blurring without picking
          // reverts the input to the previous selection.
        }}
        onFocus={(e) => {
          setOpen(true)
          e.target.select()
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className="block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
      />
      {showList && (
        <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {matches.map((a, i) => (
            <li key={a.iata}>
              <button
                type="button"
                // mousedown (not click) fires before the input blur, so the
                // tap registers instead of being swallowed by the close.
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(a)
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm ${
                  i === highlight ? 'bg-cloud' : 'bg-white'
                }`}
              >
                <span className="min-w-0 truncate text-slate-800">{cleanName(a.name)}</span>
                <span className="shrink-0 font-mono text-xs text-slate-500">
                  {a.iata}
                  {a.country ? ` · ${a.country}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
